import React, { useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import { AddwithWhiteCircle, DeleteDustbin, UpdatePencil } from '../../components/AllSvg'
import axios from 'axios';
import { BASE_URL3 } from '../../config/axios';
import { Modal } from 'react-bootstrap';
import { handleErrorToast } from '../../components/CustomFunctions';

import {
    Landmark, X, Save, Loader, AlertCircle, Building2, MapPin,
    Hash, CreditCard, FileText, Trash2, AlertTriangle,
    SectionIcon,
    Search
} from "lucide-react";
import { AddModalButton, DeleteModalButton, UpdateModalButton } from '../../components/DynamicButtons';

/* ===================== ADD + UPDATE ===================== */
const BankingCU = ({ getBankingDetail, usedFor = "Add", i }) => {
    const isAdd = usedFor === "Add";

    const initialForm = {
        account_no: "",
        bank_name: "",
        branch: "",
        gst_no: "",
        ifsc_code: "",
    };

    const [formData, setFormData] = useState(initialForm);
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const handleShow = () => {
        if (!isAdd && i) {
            setFormData({
                account_no: i.account_no ?? "",
                bank_name: i.bank_name ?? "",
                branch: i.branch ?? "",
                gst_no: i.gst_no ?? "",
                ifsc_code: i.ifsc_code ?? "",
            });
        }
        setShow(true);
    };

    const handleClose = () => {
        setShow(false);
        setFormData(initialForm);
        setErrors({});
        setInputState({});
    };

    const fields = [
        { name: "bank_name", label: "Bank Name", icon: Landmark, placeholder: "e.g. State Bank of India", type: "text" },
        { name: "branch", label: "Branch Name", icon: MapPin, placeholder: "e.g. Connaught Place, Delhi", type: "text" },
        { name: "account_no", label: "Account No.", icon: CreditCard, placeholder: "e.g. 123456789012", type: "text", inputMode: "numeric" },
        { name: "ifsc_code", label: "IFSC Code", icon: Hash, placeholder: "e.g. SBIN0001234", type: "text" },
        { name: "gst_no", label: "GST Number", icon: FileText, placeholder: "e.g. 07ABCDE1234F1Z5", type: "text", full: true },
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setInputState((prev) => ({ ...prev, [name]: value }));
        if (String(value).trim()) {
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        fields.forEach((f) => {
            if (!String(formData[f.name] ?? "").trim()) {
                newErrors[f.name] = `${f.label} is required!`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!validateForm()) return;

        const loadingToastId = toast.loading("Loading: Please wait...");
        setLoading(true);
        try {
            const res = isAdd
                ? await axios.post(`${BASE_URL3}/project/bankingname/`, formData)
                : await axios.put(`${BASE_URL3}/project/bankingnameupdate/${i.id}/`, formData);

            if (res.status === 200 || res.status === 201) {
                toast.dismiss(loadingToastId);
                toast.success(`Banking detail ${isAdd ? "added" : "updated"} successfully!`);
                handleClose();
                getBankingDetail();
            }
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const errorCount = Object.keys(errors).length;

    return (
        <>
            {isAdd ? (
                <AddModalButton title="Add Bank Details" onClick={handleShow} />

            ) : (
                <UpdateModalButton title="Update Bank Details" onClick={handleShow} />

            )}

            <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                {/* ===== HEADER ===== */}
                <Modal.Header className="modern-modal-header">
                    <div className="header-content">
                        <div className="header-icon-wrapper">
                            <Landmark size={24} />
                        </div>
                        <div>
                            <Modal.Title>{isAdd ? "Add" : "Update"} Banking Detail</Modal.Title>
                            <p className="header-subtitle">
                                {isAdd
                                    ? "Fill in the bank details to add a new account"
                                    : "Edit the bank details of this account"}
                            </p>
                        </div>
                    </div>
                    <button type="button" className="close-btn" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </Modal.Header>

                {/* ===== BODY ===== */}
                <Modal.Body className="modern-modal-body">
                    <form onSubmit={handleSubmit} className="project-form">
                        <div className="form-section animate-fade-in">
                            <div className="section-title">
                                <SectionIcon icon={Landmark} color="#3b82f6" />
                                <div>
                                    <h4>Bank Information</h4>
                                    <p className="section-subtitle">These details are shown on the quotation slip</p>
                                </div>
                            </div>

                            <div className="form-grid">
                                {fields.map((f) => {
                                    const Icon = f.icon;
                                    return (
                                        <div key={f.name} className={`form-field ${f.full ? "full-width" : ""}`}>
                                            <label htmlFor={f.name}>
                                                <Icon size={14} /> {f.label}<span className="required">*</span>
                                            </label>
                                            <input
                                                id={f.name}
                                                type={f.type}
                                                inputMode={f.inputMode}
                                                name={f.name}
                                                value={formData[f.name]}
                                                placeholder={f.placeholder}
                                                onChange={handleInputChange}
                                                className={errors[f.name] ? "error" : inputState[f.name] ? "success" : ""}
                                            />
                                            {errors[f.name] && (
                                                <span className="error-message">
                                                    <AlertCircle size={12} /> {errors[f.name]}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </form>
                </Modal.Body>

                {/* ===== FOOTER ===== */}
                <Modal.Footer className="modern-modal-footer">
                    <div>
                        {errorCount > 0 && (
                            <div className="tab-error-message">
                                <AlertCircle size={14} />
                                <span>
                                    {errorCount > 1
                                        ? `${errorCount} fields required`
                                        : "1 field required"}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="submit-buttons">
                        <button type="button" className="btn-secondaryy" onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="button" className="btn-primaryy" disabled={loading} onClick={handleSubmit}>
                            {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                            <span>
                                {loading
                                    ? (isAdd ? "Adding..." : "Updating...")
                                    : (isAdd ? "Add Banking" : "Update Banking")}
                            </span>
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

/* Purane naam se bhi use kar sakte ho, parents ko change na karna pade */
const AddBankingDetail = ({ getBankingDetail }) => (
    <BankingCU usedFor="Add" getBankingDetail={getBankingDetail} />
);

const UpdateBankingDetail = ({ i, getBankingDetail }) => (
    <BankingCU usedFor="Update" i={i} getBankingDetail={getBankingDetail} />
);

/* ===================== DELETE ===================== */
const DeleteBankingDetail = ({ i, getBankingDetail }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        setLoading(true);
        const loadingToastId = toast.loading("Loading: Please wait...");

        try {
            const res = await axios.delete(`${BASE_URL3}/project/bankingnameupdate/${i.id}/`);

            if (res.status === 200 || res.status === 204) {
                await getBankingDetail();
                handleClose();
                toast.dismiss(loadingToastId);
                toast.success("Data deleted successfully");
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <DeleteModalButton title="Delete Data" onClick={handleShow} />


            <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="md" centered>
                {/* ===== HEADER ===== */}
                <Modal.Header className="modern-modal-header delete-modal-header">
                    <div className="header-content">
                        <div className="header-icon-wrapper delete-icon-wrapper">
                            <Trash2 size={22} />
                        </div>
                        <div>
                            <Modal.Title>Delete Banking Detail</Modal.Title>
                            <p className="header-subtitle">This action cannot be undone</p>
                        </div>
                    </div>
                    <button type="button" className="close-btn" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </Modal.Header>

                {/* ===== BODY ===== */}
                <Modal.Body className="modern-modal-body">
                    <div className="delete-confirm-wrap">
                        <div className="delete-warning-icon">
                            <AlertTriangle size={34} />
                        </div>

                        <h4 className="delete-confirm-title">
                            Are you sure you want to delete this banking detail?
                        </h4>
                        <p className="delete-confirm-subtitle">
                            This bank account will be permanently removed from the system.
                        </p>

                        {(i?.bank_name || i?.account_no) && (
                            <div className="delete-preview-card">
                                <div className="delete-preview-icon">
                                    <Landmark size={16} />
                                </div>
                                <div className="delete-preview-content">
                                    <div className="delete-preview-name">
                                        <span>{i?.bank_name || "Bank"}</span>
                                        {i?.branch && <span className="delete-preview-sep">•</span>}
                                        {i?.branch && <span>{i.branch}</span>}
                                    </div>
                                    <div className="delete-preview-meta">
                                        {i?.account_no && (
                                            <span className="delete-preview-chip">
                                                <CreditCard size={12} /> {i.account_no}
                                            </span>
                                        )}
                                        {i?.ifsc_code && (
                                            <span className="delete-preview-chip">
                                                <Hash size={12} /> {i.ifsc_code}
                                            </span>
                                        )}
                                        {i?.gst_no && (
                                            <span className="delete-preview-chip">
                                                <FileText size={12} /> {i.gst_no}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="delete-warning-note">
                            <AlertCircle size={14} />
                            <span>This will permanently delete the banking detail.</span>
                        </div>
                    </div>
                </Modal.Body>

                {/* ===== FOOTER ===== */}
                <Modal.Footer className="modern-modal-footer">
                    <button type="button" className="btn-secondaryy" onClick={handleClose} disabled={loading}>
                        Cancel
                    </button>
                    <button type="button" onClick={handleFormSubmitDelete} className="btn-danger-delete" disabled={loading}>
                        {loading ? <Loader size={18} className="spinning" /> : <Trash2 size={18} />}
                        <span>{loading ? "Deleting..." : "Delete Banking"}</span>
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

// const BankingDietails = () => {

//     const [buffer, setBuffering] = useState(true);

//     const [bankingDetail, setBankingDetail] = useState([])

//     const getBankingDetail = async () => {
//         setBuffering(true);
//         try {
//             const res = await axios.get(`${BASE_URL3}/project/bankingname/`);
//             setBankingDetail(res.data);
//         } catch (err) {
//             handleErrorToast(err);
//         } finally {
//             setBuffering(false);
//         }
//     };

//     useEffect(() => {
//         getBankingDetail()
//     }, [])


//     return (
//         <>
//             <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
//             <div >
//                 <div className="attendance-box">
//                     <div className="datagrid-container">
//                         <div className="custom-table-background" style={{ height: "66vh" }}>
//                             <div className="">
//                                 <div className="">
//                                     <div className="table-box ">
//                                         <div className="flex-row table-heading" style={{ justifyContent: "end" }}>

//                                             <AddBankingDetail getBankingDetail={getBankingDetail} />
//                                         </div>
//                                     </div>
//                                     <div className='table-box scroll-container-table'
//                                     >
//                                         <table className="table-css width-100vw">
//                                             <thead className="table-heading">
//                                                 <tr className="custom-table-head-tr">
//                                                     <th className="table-heading ">S.no.</th>
//                                                     <th className="table-heading text-left">Bank Name</th>
//                                                     <th className="table-heading text-left">Branch Details</th>
//                                                     <th className="table-heading text-left">Account no.</th>
//                                                     <th className="table-heading text-left">IFCE code</th>
//                                                     <th className="table-heading text-left">GST no.</th>
//                                                     <th className="table-heading text-center">Action</th>
//                                                 </tr>
//                                             </thead>
//                                             {buffer ? (

//                                                 <div className="spinner-bgdatil"></div>

//                                             ) :
//                                                 <tbody>
//                                                     {
//                                                         bankingDetail
//                                                             .map((i, index) => (
//                                                                 <tr key={index} className="custom-table-head-td">
//                                                                     <td>{index + 1}.</td>
//                                                                     <td>{i.bank_name}</td>
//                                                                     <td>{i.branch}</td>
//                                                                     <td>{i.account_no}</td>
//                                                                     <td>{i.ifsc_code}</td>
//                                                                     <td>{i.gst_no}</td>
//                                                                     <td>
//                                                                         <div className='flex-row justify-center'>

//                                                                             <UpdateBankingDetail i={i} getBankingDetail={getBankingDetail} />
//                                                                             <DeleteBankingDetail i={i} getBankingDetail={getBankingDetail} />
//                                                                         </div>
//                                                                     </td>
//                                                                 </tr>
//                                                             ))
//                                                     }
//                                                 </tbody>

//                                             }
//                                         </table>

//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </>
//     )
// };


const BankingDietails = () => {
    const [buffer, setBuffering] = useState(true);
    const [bankingDetail, setBankingDetail] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const getBankingDetail = async () => {
        setBuffering(true);
        try {
            const res = await axios.get(`${BASE_URL3}/project/bankingname/`);
            setBankingDetail(res.data);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setBuffering(false);
        }
    };

    useEffect(() => {
        getBankingDetail();
    }, []);

    const q = searchQuery.trim().toLowerCase();
    const filteredList = (bankingDetail || []).filter((b) => {
        if (!q) return true;
        return [b.bank_name, b.branch, b.account_no, b.ifsc_code, b.gst_no].some((v) =>
            String(v ?? "").toLowerCase().includes(q)
        );
    });

    return (
        <>
            <ToastContainer
                position="top-center"
                autoClose={1000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />

            <div className="wt-page">
                <div className="wt-card">
                    {/* ===== HEADER ===== */}
                    <div className="wt-header">
                        <div className="wt-header-left">
                            <div className="wt-header-icon">
                                <Landmark size={20} />
                            </div>
                            <div>
                                <h4 className="wt-title">
                                    Banking Details
                                    <span className="wt-count">
                                        {searchQuery ? `${filteredList.length} / ${bankingDetail.length}` : bankingDetail.length}
                                    </span>
                                </h4>
                                <p className="wt-subtitle">Bank accounts shown on the quotation slip</p>
                            </div>
                        </div>

                        <div className="wt-header-right">
                            <div className="wt-search">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Search bank, branch, account..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        className="wt-search-clear"
                                        onClick={() => setSearchQuery("")}
                                        title="Clear"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            <AddBankingDetail getBankingDetail={getBankingDetail} />
                        </div>
                    </div>

                    {/* ===== TABLE ===== */}
                    <div className="wt-table-wrap">
                        <table className="wt-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 70 }}>S.No.</th>
                                    <th>Bank Name</th>
                                    <th>Branch Details</th>
                                    <th>Account No.</th>
                                    <th>IFSC Code</th>
                                    <th>GST No.</th>
                                    <th style={{ width: 140 }} className="wt-th-end">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {buffer ? (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className="wt-state">
                                                <div className="spinner-bgdatil"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredList.length === 0 ? (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className="wt-state">
                                                <Landmark size={30} />
                                                <p>{searchQuery ? "No matching bank details" : "No banking details added yet"}</p>
                                                <small>
                                                    {searchQuery
                                                        ? "Try a different search term"
                                                        : "Click the add button to create your first bank account"}
                                                </small>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredList.map((i, index) => (
                                        <tr key={i.id || index}>
                                            <td className="wt-sno">{index + 1}.</td>
                                            <td className="wt-name">{i.bank_name || "-"}</td>
                                            <td>{i.branch || "-"}</td>
                                            <td className="wt-mono">{i.account_no || "-"}</td>
                                            <td>
                                                <span className="wt-chip">{i.ifsc_code || "-"}</span>
                                            </td>
                                            <td className="wt-mono">{i.gst_no || "-"}</td>
                                            <td>
                                                <div className="wt-actions">
                                                    <UpdateBankingDetail i={i} getBankingDetail={getBankingDetail} />
                                                    <DeleteBankingDetail i={i} getBankingDetail={getBankingDetail} />
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
};


export default BankingDietails